// The day's map (Oct 23, 2026). Greg: "I would like a daily map of that days
// areas … a map at bottom showing areas for the day or week."
//
// Day view: today's daily areas in color (one color each), standing areas and
// older daily areas still open dashed, and every build / adjuster meeting /
// reinspection of the day pinned. Week view: the week's daily areas colored
// by weekday (including ones already expired, from canvas_area_review) with
// the week's stops. Also used by the Areas tab's Review with a longer window.
//
// Same Mapbox CDN global the canvas and the area drawer use. Layers are added
// in our own style-ready handler (isStyleLoaded() lies while sprites load)
// and a ResizeObserver keeps the canvas sized inside a card.

import { useEffect, useMemo, useRef, useState } from "react";
import { areaName, firstName, type AreaOutline, type AreaStats } from "../../../lib/canvasAreas";
import {
  areaDayState, colorFor, hasCoords, reviewAreaName, stopAddress, stopTitle,
  type DayStop, type ReviewArea,
} from "../../../lib/dailyAreas";

declare const mapboxgl: any;
const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

const DOW_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DOW_COLORS: Record<string, string> = {
  Mon: "#f46530", Tue: "#345c72", Wed: "#1b7fa9", Thu: "#2f855a", Fri: "#b7791f", Sat: "#7c3aed", Sun: "#c53030",
};
const STOP_COLORS: Record<DayStop["kind"], string> = { build: "#2a2e30", adjuster: "#0e7490", reinspection: "#7c3aed" };
const STOP_LABEL: Record<DayStop["kind"], string> = { build: "Build", adjuster: "Adjuster meeting", reinspection: "Reinspection" };
const MUTED = "#6b7280";
const PAST = "#b7791f";

export interface MapArea {
  id: string;
  name: string;
  who: string;
  pins: number;
  geometry: unknown;
  color: string;
  /** solid = a daily area of the window; dashed = standing or out of the window */
  dashed: boolean;
  tag: string;
  center: [number, number];
}

function dowOf(day: string): string {
  return DOW_NAMES[new Date(day + "T12:00:00Z").getUTCDay()];
}

/** Day view: active areas (stats + outlines) classified against the day on screen. */
export function dayMapAreas(areas: AreaStats[], outlines: Map<string, AreaOutline>, day: string): MapArea[] {
  let i = 0;
  const out: MapArea[] = [];
  for (const a of areas) {
    const o = outlines.get(a.area_group_id);
    if (!o) continue;
    const state = areaDayState(a, day);
    const who = a.reps.map((r) => firstName(r.full_name)).join(", ");
    const base = { id: a.area_group_id, name: areaName(a), who, pins: a.pin_count, geometry: o.geometry, center: [a.center.lon, a.center.lat] as [number, number] };
    if (state === "today") out.push({ ...base, color: colorFor(i++), dashed: false, tag: "Today" });
    else if (state === "past") out.push({ ...base, color: PAST, dashed: true, tag: `Still open from ${fmtDay(a.for_date!)}` });
    else if (state === "future") out.push({ ...base, color: MUTED, dashed: true, tag: `For ${fmtDay(a.for_date!)}` });
    else out.push({ ...base, color: MUTED, dashed: true, tag: "Standing" });
  }
  return out;
}

/** Week / review view: every area active in the window, daily ones colored by weekday. */
export function reviewMapAreas(areas: ReviewArea[], from: string, to: string): MapArea[] {
  return areas.filter((a) => a.geometry).map((a) => {
    const who = a.reps.map((r) => firstName(r.full_name)).join(", ");
    const inWindow = !!a.for_date && a.for_date >= from && a.for_date <= to;
    const dow = a.for_date ? dowOf(a.for_date) : null;
    return {
      id: a.area_group_id, name: reviewAreaName(a), who, pins: a.pin_count, geometry: a.geometry,
      center: [a.center.lon, a.center.lat] as [number, number],
      color: inWindow && dow ? DOW_COLORS[dow] : MUTED,
      dashed: !inWindow,
      tag: a.for_date ? `${dow} ${fmtDay(a.for_date)}` : "Standing",
    };
  });
}

function fmtDay(day: string): string {
  return new Date(day + "T12:00:00Z").toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
}

function coordsOf(geometry: any): Array<[number, number]> {
  if (!geometry) return [];
  if (geometry.type === "Polygon") return (geometry.coordinates ?? []).flat() as Array<[number, number]>;
  if (geometry.type === "MultiPolygon") return (geometry.coordinates ?? []).flat(2) as Array<[number, number]>;
  return [];
}

export default function DayAreasMap({ areas, stops, title, subtitle, height = "h-72 sm:h-80", right, empty }: {
  areas: MapArea[];
  stops: DayStop[];
  title?: string;
  subtitle?: string;
  height?: string;
  /** Controls drawn at the top-right of the header (a Day / Week toggle, a link). */
  right?: React.ReactNode;
  empty?: string;
}) {
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const readyRef = useRef(false);
  // Layer click handlers survive setStyle() (they are keyed by layer id), so
  // register them once or a basemap switch would open two popups per click.
  const handlersRef = useRef(false);
  // Fit the camera only when WHAT is on the map changes (an area or stop added
  // or removed), never on a live stats refresh — a manager zoomed into a
  // street must not be yanked back every few seconds while knocks come in.
  const fitKeyRef = useRef<string>("");
  const [styleTick, setStyleTick] = useState(0);
  const [satellite, setSatellite] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pinned = useMemo(() => stops.filter(hasCoords), [stops]);
  const nothing = areas.length === 0 && pinned.length === 0;

  // Init once.
  useEffect(() => {
    if (!mapDiv.current || mapRef.current) return;
    if (typeof mapboxgl === "undefined" || !TOKEN) { setErr("Map isn't available here."); return; }
    mapboxgl.accessToken = TOKEN;
    const map = new mapboxgl.Map({
      container: mapDiv.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [-84.5, 34.0],
      zoom: 9.3,
      attributionControl: false,
      cooperativeGestures: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    map.on("style.load", () => { readyRef.current = true; setStyleTick((n) => n + 1); });
    mapRef.current = map;
    const ro = new ResizeObserver(() => { try { map.resize(); } catch { /* ignore */ } });
    ro.observe(mapDiv.current);
    return () => { ro.disconnect(); try { map.remove(); } catch { /* ignore */ } mapRef.current = null; readyRef.current = false; };
  }, []);

  // Basemap toggle.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    readyRef.current = false;
    map.setStyle(satellite ? "mapbox://styles/mapbox/satellite-streets-v12" : "mapbox://styles/mapbox/streets-v12");
  }, [satellite]);

  // Draw / redraw whenever the data or the style changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;

    const areaFc = {
      type: "FeatureCollection",
      features: areas.map((a) => ({
        type: "Feature",
        properties: { id: a.id, name: a.name, who: a.who, pins: a.pins, color: a.color, dashed: a.dashed, tag: a.tag },
        geometry: a.geometry,
      })),
    };
    const labelFc = {
      type: "FeatureCollection",
      features: areas.map((a) => ({
        type: "Feature",
        properties: { label: a.who ? `${a.name}\n${a.who} · ${a.pins} pins` : `${a.name}\n${a.pins} pins`, color: a.color },
        geometry: { type: "Point", coordinates: a.center },
      })),
    };
    const stopFc = {
      type: "FeatureCollection",
      features: pinned.map((s) => ({
        type: "Feature",
        properties: {
          title: stopTitle(s), address: stopAddress(s), kind: STOP_LABEL[s.kind], color: STOP_COLORS[s.kind],
          label: stopTitle(s),
        },
        geometry: { type: "Point", coordinates: [s.lon, s.lat] },
      })),
    };

    const upsert = (id: string, data: unknown) => {
      const src = map.getSource(id);
      if (src) src.setData(data); else map.addSource(id, { type: "geojson", data });
    };
    upsert("da-areas", areaFc);
    upsert("da-labels", labelFc);
    upsert("da-stops", stopFc);

    if (!map.getLayer("da-areas-fill")) {
      map.addLayer({
        id: "da-areas-fill", type: "fill", source: "da-areas",
        paint: { "fill-color": ["get", "color"], "fill-opacity": ["case", ["get", "dashed"], 0.07, 0.2] },
      });
      map.addLayer({
        id: "da-areas-line-solid", type: "line", source: "da-areas", filter: ["==", ["get", "dashed"], false],
        paint: { "line-color": ["get", "color"], "line-width": 2.5 },
      });
      map.addLayer({
        id: "da-areas-line-dashed", type: "line", source: "da-areas", filter: ["==", ["get", "dashed"], true],
        paint: { "line-color": ["get", "color"], "line-width": 1.5, "line-dasharray": [2, 2] },
      });
      map.addLayer({
        id: "da-labels-text", type: "symbol", source: "da-labels",
        layout: { "text-field": ["get", "label"], "text-size": 11, "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"], "text-anchor": "center", "text-allow-overlap": false },
        paint: { "text-color": ["get", "color"], "text-halo-color": "#ffffff", "text-halo-width": 1.5 },
      });
      map.addLayer({
        id: "da-stops-pts", type: "circle", source: "da-stops",
        paint: { "circle-radius": 7, "circle-color": ["get", "color"], "circle-stroke-color": "#ffffff", "circle-stroke-width": 2 },
      });
      map.addLayer({
        id: "da-stops-text", type: "symbol", source: "da-stops",
        layout: { "text-field": ["get", "label"], "text-size": 10.5, "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"], "text-offset": [0, 1.3], "text-anchor": "top", "text-allow-overlap": false },
        paint: { "text-color": "#2a2e30", "text-halo-color": "#ffffff", "text-halo-width": 1.5 },
      });
    }
    if (!handlersRef.current) {
      handlersRef.current = true;
      map.on("click", "da-areas-fill", (e: any) => {
        const p = e.features?.[0]?.properties ?? {};
        new mapboxgl.Popup({ closeButton: false }).setLngLat(e.lngLat)
          .setHTML(`<div style="font-family:system-ui;font-size:12px"><strong>${esc(p.name)}</strong><br/>${esc(p.tag)}${p.who ? ` · ${esc(p.who)}` : ""}<br/>${p.pins} pins</div>`)
          .addTo(map);
      });
      map.on("click", "da-stops-pts", (e: any) => {
        const p = e.features?.[0]?.properties ?? {};
        const c = e.features?.[0]?.geometry?.coordinates;
        if (!c) return;
        new mapboxgl.Popup({ closeButton: false }).setLngLat(c)
          .setHTML(`<div style="font-family:system-ui;font-size:12px"><strong>${esc(p.title)}</strong><br/>${esc(p.kind)}<br/>${esc(p.address)}</div>`)
          .addTo(map);
      });
      for (const id of ["da-areas-fill", "da-stops-pts"]) {
        map.on("mouseenter", id, () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", id, () => { map.getCanvas().style.cursor = ""; });
      }
    }

    // Fit to everything on the map — only when the set of things changed.
    const fitKey = [...areas.map((a) => a.id).sort(), "|", ...pinned.map((s) => `${s.deal_id}:${s.kind}`).sort()].join(",");
    if (fitKey !== fitKeyRef.current) {
      fitKeyRef.current = fitKey;
      const pts: Array<[number, number]> = [
        ...areas.flatMap((a) => coordsOf(a.geometry)),
        ...pinned.map((s) => [s.lon, s.lat] as [number, number]),
      ];
      if (pts.length > 0) {
        const b = pts.reduce((bb: any, c) => bb.extend(c), new mapboxgl.LngLatBounds(pts[0], pts[0]));
        map.fitBounds(b, { padding: 40, duration: 0, maxZoom: 14.5 });
      }
    }
  }, [areas, pinned, styleTick]);

  const legendAreas = areas.filter((a) => !a.dashed);
  const dashedKinds = Array.from(new Set(areas.filter((a) => a.dashed).map((a) => a.tag.startsWith("Still open") ? "Still open from an earlier day" : a.tag.startsWith("For ") ? "For a later day" : "Standing")));
  const stopKinds = (["build", "adjuster", "reinspection"] as const).map((k) => ({ k, n: pinned.filter((s) => s.kind === k).length })).filter((x) => x.n > 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      {(title || right) && (
        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
          <div>
            {title && <div className="text-sm font-bold text-slate-900">{title}</div>}
            {subtitle && <div className="text-[11px] text-slate-500">{subtitle}</div>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {right}
            <button type="button" onClick={() => setSatellite((v) => !v)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50"
              title="Switch the basemap">
              {satellite ? "🗺 Streets" : "🛰 Satellite"}
            </button>
          </div>
        </div>
      )}
      <div className="relative">
        <div ref={mapDiv} className={`w-full overflow-hidden rounded-lg border border-slate-200 ${height}`} />
        {nothing && !err && (
          <div className="pointer-events-none absolute inset-x-0 top-3 mx-auto w-max max-w-[90%] rounded-md bg-white/90 px-3 py-1.5 text-center text-xs text-slate-600 shadow">
            {empty ?? "Nothing on the map yet."}
          </div>
        )}
        {err && <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-slate-50 text-xs text-slate-500">{err}</div>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600">
        {legendAreas.map((a) => (
          <span key={a.id} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: a.color }} />
            <b className="text-slate-800">{a.name}</b>{a.who ? ` · ${a.who}` : ""}
          </span>
        ))}
        {dashedKinds.map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm border border-dashed border-slate-500" />{k}
          </span>
        ))}
        {stopKinds.map(({ k, n }) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-full border-2 border-white shadow" style={{ background: STOP_COLORS[k] }} />
            {n} {STOP_LABEL[k].toLowerCase()}{n === 1 ? "" : "s"}
          </span>
        ))}
      </div>
    </div>
  );
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}
